import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { getBackendOrigin } from '../utils/apiOrigin';
import { SOCKET_IO_PATH } from '../utils/socketConfig';

export type ChatSocketHandlers = {
  onMessage?: (payload: { conversationId: string; message: unknown }) => void;
  onConversationUpdated?: (payload: { conversation: unknown }) => void;
  onTyping?: (payload: {
    conversationId: string;
    userId: string;
    role: string;
    isTyping: boolean;
  }) => void;
  onConnect?: () => void;
};

export const useChatSocket = (
  token: string | null,
  handlers: ChatSocketHandlers,
  enabled = true
) => {
  const socketRef = useRef<Socket | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const pendingJoinRef = useRef<string | null>(null);

  const emitTyping = useCallback((conversationId: string, isTyping: boolean) => {
    socketRef.current?.emit('chat:typing', { conversationId, isTyping });
  }, []);

  const joinConversation = useCallback((conversationId: string) => {
    pendingJoinRef.current = conversationId;
    if (socketRef.current?.connected) {
      socketRef.current.emit('chat:join-conversation', { conversationId });
    }
  }, []);

  const leaveConversation = useCallback((conversationId: string) => {
    if (pendingJoinRef.current === conversationId) {
      pendingJoinRef.current = null;
    }
    socketRef.current?.emit('chat:leave-conversation', { conversationId });
  }, []);

  useEffect(() => {
    if (!enabled || !token) return;

    const socket = io(getBackendOrigin(), {
      path: SOCKET_IO_PATH,
      transports: ['websocket', 'polling'],
      auth: { token },
    });

    socket.on('connect', () => {
      handlersRef.current.onConnect?.();
      if (pendingJoinRef.current) {
        socket.emit('chat:join-conversation', { conversationId: pendingJoinRef.current });
      }
    });

    socket.on('chat:message', (payload) => handlersRef.current.onMessage?.(payload));
    socket.on('chat:conversation-updated', (payload) =>
      handlersRef.current.onConversationUpdated?.(payload)
    );
    socket.on('chat:typing', (payload) => handlersRef.current.onTyping?.(payload));

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, enabled]);

  return { emitTyping, joinConversation, leaveConversation, socket: socketRef };
};

export default useChatSocket;
